import { StatusCode } from "api/protocols";
import { PostRefund } from "api/rpc/2024-04/facilityAdmin/order/refund";
import { GetOrder } from "api/rpc/2024-04/facilityAdmin/order/order";
import { delay } from "helpers/Helpers";
import {
  getRefundablePaymentTransactions,
  IRefundablePaymentTransaction,
} from "helpers/orderFinancial";
import { IOrder, IOrderTransaction } from "redux/reducers/models/order";

export type ProcessInteracRefund = (amount: number, paymentIntentId: string) => Promise<string | void>;

interface IRefundOrderPaymentsParams {
  orderId: number;
  /** Prefer passing current transactions; order will be reloaded if missing/empty. */
  transactions?: IOrderTransaction[] | null;
  processInteracRefund?: ProcessInteracRefund;
  hasReader?: boolean;
  note?: string;
}

export interface IRefundOrderPaymentsResult {
  ok: boolean;
  message?: string;
  order?: IOrder;
  refundedTransactions?: IRefundablePaymentTransaction[];
}

/**
 * Refunds all remaining collected tenders on an order (payment-only, no line items).
 * Card (non-Interac) is reversed via Stripe in the API. Interac requires a connected reader.
 * Cash/check/gift card/account are handled by the refund API / staff.
 */
export async function refundOrderPayments(
  params: IRefundOrderPaymentsParams,
): Promise<IRefundOrderPaymentsResult> {
  const { orderId, processInteracRefund, hasReader = false, note } = params;

  let transactions = params.transactions ?? [];
  let order: IOrder | undefined;

  if (!transactions.length) {
    const orderRes = await GetOrder({ id: orderId, extended: true }, true);
    if (orderRes.status !== StatusCode.OK || !orderRes.data?.[0]) {
      return { ok: false, message: "Unable to load order payments for refund." };
    }
    order = orderRes.data[0];
    transactions = order.transactions ?? [];
  }

  const refundable = getRefundablePaymentTransactions(transactions);
  if (!refundable.length) {
    return { ok: false, message: "No refundable payments found on this order." };
  }

  const interacRefunds = refundable.filter(
    transaction => transaction.payment_type === "interac" && Number(transaction.amount) > 0,
  );

  if (interacRefunds.length > 0) {
    if (!hasReader || !processInteracRefund) {
      return {
        ok: false,
        message: "Connect a card reader to refund Interac payments before cancelling this order.",
      };
    }

    for (const transaction of interacRefunds) {
      if (!transaction.payment_intent_id) {
        return { ok: false, message: "Interac payment is missing a payment intent and cannot be refunded." };
      }

      await delay(2000);
      const refundRes = await processInteracRefund(
        Number(transaction.amount),
        String(transaction.payment_intent_id),
      );

      if (refundRes !== "success") {
        return { ok: false, message: "Interac refund failed. Order was not cancelled." };
      }
    }
  }

  const refundRes = await PostRefund(
    {
      order_id: orderId,
      refund_line_items: [],
      refund_transactions: refundable.map(transaction => ({
        parent_id: transaction.parent_id,
        amount: transaction.amount,
      })),
      note: note ?? "Refunded payments on cancel/void",
    },
    true,
  );

  if (refundRes.status !== StatusCode.OK) {
    return {
      ok: false,
      message: refundRes.message || "Failed to refund order payments.",
    };
  }

  return {
    ok: true,
    order,
    refundedTransactions: refundable,
  };
}
